import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumCatalogue, AlbumSlotFrame, AlbumTemplate, AlbumTemplatePlace } from '../../types';

interface Props {
  catalogue: AlbumCatalogue;
  value: AlbumTemplate | null;
  onChange: (template: AlbumTemplate) => void;
  /** ADR-0052: which places this page can take — a spread layout only on an inner spread, the wrap only on the cover. */
  fits: (place: AlbumTemplatePlace) => boolean;
  /** On the cover the cover layouts come first; anywhere else, last. */
  onCover: boolean;
}

type CatalogueTemplate = AlbumCatalogue['templates'][number];

const KIND_FILL = { PHOTO: 'bg-slate-300 dark:bg-slate-500', TITLE: 'bg-slate-500 dark:bg-slate-300', TEXT: 'bg-slate-200 dark:bg-slate-600', MAP: 'bg-teal-200 dark:bg-teal-800' } as const;

const SECTIONS = ['covers', '1', '2', '3', '4+', 'text', 'map', 'spread'] as const;
type Section = typeof SECTIONS[number];

/** Like the photo-book editors: covers, layouts by how many photos they take, text, maps, the double pages. */
function sectionOf(tp: CatalogueTemplate): Section {
  if (tp.cover) return 'covers';
  if (tp.place !== 'page') return 'spread';
  if (tp.id === 'STATS' || tp.slots.some(f => f.kind === 'MAP')) return 'map';
  const photos = tp.slots.filter(f => f.kind === 'PHOTO').length;
  if (photos === 0) return 'text';
  return photos >= 4 ? '4+' : (String(photos) as '1' | '2' | '3');
}

/** Every layout as a wireframe drawn from the server's frames (ADR-0046), in titled sections. */
export default function AlbumTemplatePicker({ catalogue, value, onChange, fits, onCover }: Props) {
  const { t } = useTranslation();
  const sectionRefs = useRef(new Map<Section, HTMLElement>());
  const H = catalogue.page.height;
  const widthOf = (place: AlbumTemplatePlace) =>
    place === 'page' ? catalogue.page.width : 2 * catalogue.page.width + (place === 'cover' ? catalogue.spine.width : 0);
  const usable = catalogue.templates.filter(tp => fits(tp.place));
  const order: Section[] = onCover ? [...SECTIONS] : [...SECTIONS.filter(s => s !== 'covers'), 'covers'];
  // A section with nothing this page can take isn't shown.
  const sections = order
    .map(section => ({ section, templates: usable.filter(tp => sectionOf(tp) === section) }))
    .filter(s => s.templates.length > 0);

  return (
    <div className="space-y-3">
      <nav
        className="md:sticky md:top-0 z-10 -mx-1 px-1 py-1 flex flex-wrap gap-1 bg-white/95 dark:bg-slate-900/95 backdrop-blur"
        aria-label={t('album.templateGroups.label')}
      >
        {sections.map(({ section }) => (
          <button
            key={section}
            type="button"
            onClick={() => sectionRefs.current.get(section)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            {t(`album.templateGroups.${section}`)}
          </button>
        ))}
      </nav>
      <div role="radiogroup" aria-label={t('album.template')} className="space-y-4">
        {sections.map(({ section, templates }) => (
          <section
            key={section}
            ref={el => { if (el) sectionRefs.current.set(section, el); else sectionRefs.current.delete(section); }}
            className="scroll-mt-10"
            aria-labelledby={`album-templates-${section}`}
          >
            <h4 id={`album-templates-${section}`} className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {t(`album.templateSections.${section}`)}
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {templates.map(tp => {
                const W = widthOf(tp.place);
                return (
                  <button
                    key={tp.id}
                    role="radio"
                    aria-checked={value === tp.id}
                    title={t(`album.templates.${tp.id}`)}
                    onClick={() => onChange(tp.id)}
                    className={`rounded-md p-1 cursor-pointer ${tp.place !== 'page' ? 'col-span-2' : ''} ${value === tp.id ? 'ring-2 ring-teal-500' : 'ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-teal-300'}`}
                  >
                    <Wireframe slots={tp.slots} width={W} height={H} spread={tp.place !== 'page'} />
                    <span className="block mt-1 text-[9px] leading-tight text-slate-500 dark:text-slate-400 truncate">{t(`album.templates.${tp.id}`)}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Wireframe({ slots, width: W, height: H, spread }: { slots: AlbumSlotFrame[]; width: number; height: number; spread: boolean }) {
  return (
    <div className="relative w-full bg-white dark:bg-slate-800 overflow-hidden" style={{ aspectRatio: `${W} / ${H}` }}>
      {slots.map((f, i) => (
        <div
          key={i}
          className={`absolute rounded-[1px] ${f.onPhoto ? 'bg-slate-100/80 dark:bg-slate-900/60' : KIND_FILL[f.kind]}`}
          style={{ left: `${(f.x / W) * 100}%`, top: `${(f.y / H) * 100}%`, width: `${(f.w / W) * 100}%`, height: `${(f.h / H) * 100}%` }}
        />
      ))}
      {spread && <div className="absolute inset-y-0 left-1/2 border-l border-dashed border-slate-400/70" />}
    </div>
  );
}
